/**
 * Marketplace Store
 * Zustand store for managing marketplace state
 */

import { create } from 'zustand';
import type {
  MarketplaceListing,
  CreateListingInput,
  UpdateListingInput,
  MarketplaceInstall,
  InstallOptions,
  MarketplaceReview,
  CreateReviewInput,
  MarketplaceCategory,
  SearchParams,
} from './types';
import * as api from './api';

interface MarketplaceState {
  // Browse state
  listings: MarketplaceListing[];
  listingsLoading: boolean;
  listingsError: string | null;
  currentListing: MarketplaceListing | null;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };

  // Featured
  featured: {
    featured: MarketplaceListing[];
    popular: MarketplaceListing[];
    newest: MarketplaceListing[];
    top_rated: MarketplaceListing[];
  } | null;
  featuredLoading: boolean;

  // Categories
  categories: MarketplaceCategory[];

  // Search
  searchParams: SearchParams;

  // Reviews
  reviews: MarketplaceReview[];
  reviewsLoading: boolean;

  // My Installations
  myInstalls: MarketplaceInstall[];
  installsLoading: boolean;

  // My Listings (Publisher)
  myListings: MarketplaceListing[];
  myListingsLoading: boolean;

  // Actions - Browse
  search: (params?: SearchParams) => Promise<void>;
  loadMore: () => Promise<void>;
  loadFeatured: () => Promise<void>;
  loadCategories: () => Promise<void>;
  loadListing: (slug: string) => Promise<MarketplaceListing>;
  setSearchParams: (params: Partial<SearchParams>) => void;
  clearSearch: () => void;

  // Actions - Install
  install: (listingId: string, options?: InstallOptions) => Promise<MarketplaceInstall>;
  uninstall: (listingId: string) => Promise<void>;
  loadMyInstalls: () => Promise<void>;

  // Actions - Reviews
  loadReviews: (listingId: string, params?: { sort_by?: 'helpful' | 'newest' | 'rating' }) => Promise<void>;
  submitReview: (listingId: string, review: CreateReviewInput) => Promise<void>;
  voteReview: (reviewId: string, isHelpful: boolean) => Promise<void>;

  // Actions - Publisher
  loadMyListings: () => Promise<void>;
  createListing: (input: CreateListingInput) => Promise<MarketplaceListing>;
  updateListing: (listingId: string, input: UpdateListingInput) => Promise<MarketplaceListing>;
  deleteListing: (listingId: string) => Promise<void>;
  submitForReview: (listingId: string) => Promise<void>;
  publishVersion: (listingId: string, version: string, changelog?: string) => Promise<void>;

  clearErrors: () => void;
}

export const useMarketplaceStore = create<MarketplaceState>((set, get) => ({
  // Initial state
  listings: [],
  listingsLoading: false,
  listingsError: null,
  currentListing: null,
  pagination: {
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false,
  },

  featured: null,
  featuredLoading: false,

  categories: [],

  searchParams: {},

  reviews: [],
  reviewsLoading: false,

  myInstalls: [],
  installsLoading: false,

  myListings: [],
  myListingsLoading: false,

  // Browse Actions
  search: async (params?: SearchParams) => {
    const searchParams = params || get().searchParams;
    set({ listingsLoading: true, listingsError: null, searchParams });

    try {
      const response = await api.searchListings(searchParams);
      set({
        listings: response.data,
        pagination: response.pagination,
        listingsLoading: false,
      });
    } catch (error) {
      set({
        listingsError: (error as Error).message,
        listingsLoading: false,
      });
    }
  },

  loadMore: async () => {
    const { searchParams, pagination, listings } = get();
    if (!pagination.hasMore) return;

    set({ listingsLoading: true });
    try {
      const response = await api.searchListings({
        ...searchParams,
        offset: pagination.offset + pagination.limit,
      });
      set({
        listings: [...listings, ...response.data],
        pagination: response.pagination,
        listingsLoading: false,
      });
    } catch (error) {
      set({
        listingsError: (error as Error).message,
        listingsLoading: false,
      });
    }
  },

  loadFeatured: async () => {
    set({ featuredLoading: true });
    try {
      const response = await api.getFeatured();
      set({ featured: response.data, featuredLoading: false });
    } catch (error) {
      set({ featuredLoading: false });
    }
  },

  loadCategories: async () => {
    try {
      const response = await api.getCategories();
      set({ categories: response.data });
    } catch (error) {
      // Silently fail for categories
    }
  },

  loadListing: async (slug: string) => {
    try {
      const response = await api.getListing(slug);
      set({ currentListing: response.data });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  setSearchParams: (params: Partial<SearchParams>) => {
    set((state) => ({
      searchParams: { ...state.searchParams, ...params, offset: 0 },
    }));
  },

  clearSearch: () => {
    set({
      searchParams: {},
      listings: [],
      pagination: { total: 0, limit: 20, offset: 0, hasMore: false },
    });
  },

  // Install Actions
  install: async (listingId: string, options?: InstallOptions) => {
    set({ installsLoading: true });
    try {
      const response = await api.installListing(listingId, options);
      set((state) => ({
        myInstalls: [...state.myInstalls, response.data],
        installsLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({ installsLoading: false });
      throw error;
    }
  },

  uninstall: async (listingId: string) => {
    set({ installsLoading: true });
    try {
      await api.uninstallListing(listingId);
      set((state) => ({
        myInstalls: state.myInstalls.filter((i) => i.listing_id !== listingId),
        installsLoading: false,
      }));
    } catch (error) {
      set({ installsLoading: false });
      throw error;
    }
  },

  loadMyInstalls: async () => {
    set({ installsLoading: true });
    try {
      const response = await api.getMyInstalls();
      set({ myInstalls: response.data, installsLoading: false });
    } catch (error) {
      set({ installsLoading: false });
      throw error;
    }
  },

  // Review Actions
  loadReviews: async (listingId: string, params?) => {
    set({ reviewsLoading: true });
    try {
      const response = await api.getReviews(listingId, params);
      set({ reviews: response.data, reviewsLoading: false });
    } catch (error) {
      set({ reviewsLoading: false });
      throw error;
    }
  },

  submitReview: async (listingId: string, review: CreateReviewInput) => {
    try {
      const response = await api.createReview(listingId, review);
      set((state) => ({
        reviews: [response.data, ...state.reviews],
      }));
    } catch (error) {
      throw error;
    }
  },

  voteReview: async (reviewId: string, isHelpful: boolean) => {
    try {
      await api.voteReview(reviewId, isHelpful);
      set((state) => ({
        reviews: state.reviews.map((r) =>
          r.id === reviewId
            ? {
                ...r,
                helpful_count: isHelpful ? r.helpful_count + 1 : r.helpful_count,
                user_voted: isHelpful,
              }
            : r
        ),
      }));
    } catch (error) {
      throw error;
    }
  },

  // Publisher Actions
  loadMyListings: async () => {
    set({ myListingsLoading: true });
    try {
      const response = await api.getMyListings();
      set({ myListings: response.data, myListingsLoading: false });
    } catch (error) {
      set({ myListingsLoading: false });
      throw error;
    }
  },

  createListing: async (input: CreateListingInput) => {
    try {
      const response = await api.createListing(input);
      set((state) => ({
        myListings: [...state.myListings, response.data],
      }));
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateListing: async (listingId: string, input: UpdateListingInput) => {
    try {
      const response = await api.updateListing(listingId, input);
      set((state) => ({
        myListings: state.myListings.map((l) =>
          l.id === listingId ? response.data : l
        ),
        currentListing:
          state.currentListing?.id === listingId ? response.data : state.currentListing,
      }));
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteListing: async (listingId: string) => {
    try {
      await api.deleteListing(listingId);
      set((state) => ({
        myListings: state.myListings.filter((l) => l.id !== listingId),
      }));
    } catch (error) {
      throw error;
    }
  },

  submitForReview: async (listingId: string) => {
    try {
      const response = await api.submitForReview(listingId);
      set((state) => ({
        myListings: state.myListings.map((l) =>
          l.id === listingId ? response.data : l
        ),
      }));
    } catch (error) {
      throw error;
    }
  },

  publishVersion: async (listingId: string, version: string, changelog?: string) => {
    try {
      const response = await api.publishVersion(listingId, version, changelog);
      set((state) => ({
        myListings: state.myListings.map((l) =>
          l.id === listingId ? response.data : l
        ),
      }));
    } catch (error) {
      throw error;
    }
  },

  clearErrors: () => {
    set({ listingsError: null });
  },
}));

// Selectors
export const useListings = () => useMarketplaceStore((state) => state.listings);
export const useCurrentListing = () => useMarketplaceStore((state) => state.currentListing);
export const useFeaturedListings = () => useMarketplaceStore((state) => state.featured);
export const useCategories = () => useMarketplaceStore((state) => state.categories);
export const useMyInstalls = () => useMarketplaceStore((state) => state.myInstalls);
export const useMyListings = () => useMarketplaceStore((state) => state.myListings);
export const useReviews = () => useMarketplaceStore((state) => state.reviews);

// Check if listing is installed
export const useIsInstalled = (listingId: string) =>
  useMarketplaceStore((state) => state.myInstalls.some((i) => i.listing_id === listingId));
